// ============================================
// FONCTIONS DE FORMATAGE (Style VB.NET)
// ============================================

// Premiere lettre majuscule pour chaque mot (noms, prenoms, villes)
export const formatNomPropre = (value) => {
  if (!value) return '';
  return value
    .toLowerCase()
    .split(/(\s+|-)/)
    .map(word => {
      if (word.match(/^\s+$/) || word === '-') return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join('');
};

// Format N.A.S. : XXX-XXX-XXX (9 chiffres)
export const formatNAS = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Validation N.A.S. selon algorithme Luhn (comme dans VB.NET ValidNoAssSoc)
export const validateNAS = (nas) => {
  const digits = nas.replace(/-/g, '');
  if (!digits || digits.trim() === '' || digits === '000000000') {
    return { valid: true, message: '' };
  }
  if (digits.length !== 9) {
    return { valid: false, message: 'Le N.A.S. doit contenir 9 chiffres' };
  }
  if (!/^\d{9}$/.test(digits)) {
    return { valid: false, message: 'Le N.A.S. doit contenir uniquement des chiffres' };
  }
  let total = 0;
  for (let i = 0; i < 8; i++) {
    let c = parseInt(digits[i], 10);
    if (i % 2 === 1) {
      c = c * 2;
      if (c > 9) {
        c = Math.floor(c / 10) + (c % 10);
      }
    }
    total += c;
  }
  const checkDigit = (10 - (total % 10)) % 10;
  const lastDigit = parseInt(digits[8], 10);
  if (checkDigit !== lastDigit) {
    return { valid: false, message: 'Numero d\'assurance sociale incorrect. Veuillez verifier.' };
  }
  return { valid: true, message: '' };
};

// Format Code postal canadien : A1A 1A1
export const formatCodePostal = (value) => {
  if (!value) return '';
  const clean = value.replace(/\s/g, '').toUpperCase().slice(0, 6);
  let result = '';
  for (let i = 0; i < clean.length; i++) {
    const char = clean[i];
    if (i % 2 === 0) {
      if (/[A-Z]/.test(char)) result += char;
    } else {
      if (/[0-9]/.test(char)) result += char;
    }
  }
  if (result.length > 3) {
    return `${result.slice(0, 3)} ${result.slice(3)}`;
  }
  return result;
};

// Format telephone : ### ###-#### (masque VB.NET)
export const formatTelephone = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Format adresse : Premiere lettre majuscule
export const formatAdresse = (value) => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

// Format montant : Enlever les caracteres non numeriques
export const formatMontant = (value) => {
  if (!value && value !== 0) return '';
  const num = parseFloat(String(value).replace(/[^\d.-]/g, ''));
  return isNaN(num) ? 0 : num;
};

// Format montant en dollars canadiens (ex: 9 999.99 $)
export const formatMontantCAD = (value) => {
  if (value === '' || value === null || value === undefined || value === 0) return '';
  const num = parseFloat(String(value).replace(/[^\d.-]/g, '').replace(',', '.'));
  if (isNaN(num)) return '';
  const formatted = num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return formatted + ' $';
};

// Parser le montant depuis le format CAD
export const parseMontantCAD = (value) => {
  if (!value) return 0;
  const clean = String(value).replace(/[^\d.]/g, '');
  const num = parseFloat(clean);
  return isNaN(num) ? 0 : num;
};

// Format S.E.D. : >######? (6 chiffres + 1 caractere optionnel, majuscules)
export const formatSED = (value) => {
  if (!value) return '';
  const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return clean.slice(0, 7);
};

// Format No Bande : TextBox simple, 20 caracteres max
export const formatNoBande = (value) => {
  if (!value) return '';
  return value.slice(0, 20);
};

// Format No Aide Sociale (No SR) : ????######aa
export const formatNoAideSociale = (value) => {
  if (!value) return '';
  const clean = value.toUpperCase();
  let result = '';
  let letterCount1 = 0;
  let digitCount = 0;
  let letterCount2 = 0;
  for (const char of clean) {
    if (letterCount1 < 4 && /[A-Z]/.test(char) && digitCount === 0) {
      result += char;
      letterCount1++;
    } else if (digitCount < 6 && /[0-9]/.test(char)) {
      result += char;
      digitCount++;
    } else if (digitCount === 6 && letterCount2 < 2 && /[A-Z]/.test(char)) {
      result += char;
      letterCount2++;
    }
  }
  return result;
};


// Calculer l'age exact a partir de la date de naissance
export const calculerAge = (dateNaissance) => {
  if (!dateNaissance) return null;
  const naissance = new Date(dateNaissance + 'T00:00:00');
  if (isNaN(naissance.getTime())) return null;
  const aujourdhui = new Date();
  let age = aujourdhui.getFullYear() - naissance.getFullYear();
  const moisDiff = aujourdhui.getMonth() - naissance.getMonth();
  if (moisDiff < 0 || (moisDiff === 0 && aujourdhui.getDate() < naissance.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
};
